import React, {useEffect, useState, useCallback, useMemo} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import AxiosService from '../services/APIService';
import {Button, Card, Col, DatePicker, Descriptions, Flex, Form, Image, Input, InputNumber, List, message, Modal, Row, Select, Space, Typography} from 'antd';
import moment from 'moment';
import {connect} from 'react-redux';
import {DownloadOutlined, EditOutlined, EyeOutlined, FileTextOutlined, LeftOutlined, LockOutlined} from '@ant-design/icons';
import useFile from '../hooks/useFile';
import {getDocURL} from '../utils/s3Service';
import icon1 from '../assets/icon1.svg';
import icon2 from '../assets/icon2.svg';
import icon3 from '../assets/icon3.svg';
import icon4 from '../assets/icon4.svg';

// Common modal component to reduce repetition
const EditModal = ({title, open, onCancel, onFinish, form, children}) => (
  <Modal 
    title={title}
    open={open}
    onCancel={onCancel}
    footer={[]}
    width={600}
  >
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
    >
      {children}
      <Button type="primary" htmlType="submit">Submit</Button>
    </Form>
  </Modal>
);

const UserProfile = ({user, customer, isLoading}) => {
    const { fileList } = useFile();
    const [searchParams] = useSearchParams();
    const userId = searchParams.get("userId");
    const [userData, setUserData] = useState(null);
    const [profitData, setProfitData] = useState(null);
    const [previewImg, setPreviewImg] = useState("");
    const [previewShow, setPreviewShow] = useState(false);
    
    // Modal states
    const [modalState, setModalState] = useState({
      editRequest: false,
      updateFile: false,
      updateSetting: false,
      updateCharge: false,
      changePassword: false
    });
    
    // Forms
    const [editRequestForm] = Form.useForm();
    const [updateForm] = Form.useForm();
    const [passwordForm] = Form.useForm();
    
    const axiosService = new AxiosService();
    const editAllowRole = ["ADMIN", "WHITE_LABEL"];
    
    // Memoized user ID based on role
    const effectiveUserId = useMemo(() => {
      if (user?.role === "ADMIN" || user?.role === "WHITE_LABEL") {
        return userId;
      } else if (user?.role === "CUSTOMER") {
        return customer?._id;
      }
      return null;
    }, [user, userId, customer]);
    
    // Fetch user data on component mount or when effectiveUserId changes
    useEffect(() => {
      if (effectiveUserId) {
        fetchCustomerDetail(effectiveUserId);
      }
    }, [effectiveUserId]);
    
    // Fetch dashboard data when userData changes
    useEffect(() => {
      if (userData) {
        fetchDashboardData({});
      }
    }, [userData]);
    
    // API calls
    const fetchCustomerDetail = useCallback((userID) => {
      axiosService.get(`/customer/get/${userID}`)
        .then(res => {
          setUserData(res.data);
        })
        .catch(e => console.log(e));
    }, []);
    
    const fetchDashboardData = useCallback(({startDate, endDate}) => {
      if (!userData?.user?._id) return;
      
      axiosService.get(`/customer/dashboard/${userData.user._id}`, {
        startDate,
        endDate
      })
        .then(res => {
          setProfitData(res);
        })
        .catch(e => {
          console.log(e);
        });
    }, [userData]);
    
    // Modal handlers
    const openModal = useCallback((modalName) => {
      setModalState(prev => ({...prev, [modalName]: true}));
    }, []);
    
    const closeModal = useCallback((modalName) => {
      setModalState(prev => ({...prev, [modalName]: false}));
    }, []);
    
    // Form submission handlers
    const handleEditRequestSubmit = useCallback((val) => {
      axiosService.post('/customer/generate-edit-req', {
        note: val.request
      })
        .then(res => {
          message.success(res.message);
          closeModal('editRequest');
          editRequestForm.resetFields();
        })
        .catch(e => message.error(e.message));
    }, []);
    
    const handleFileSubmit = useCallback((val) => {
      axiosService.put(`/customer/set-file/${userData._id}`, {
        file: val.fileName
      })
        .then(res => {
          message.success(res.message);
          closeModal('updateFile');
          updateForm.resetFields();
        })
        .catch(e => {
          message.error(e.message);
        });
    }, [userData]);
    
    const handleSettingSubmit = useCallback((val) => {
      axiosService.put(`/customer/save-setting/${userData._id}`, {
        setting: val.setting
      })
        .then(res => {
          message.success(res.message);
          closeModal('updateSetting');
          updateForm.resetFields();
        })
        .catch(e => {
          message.error(e.message);
        });
    }, [userData]);
    
    const handleChargeSubmit = useCallback((val) => {
      axiosService.put(`/customer/set-botcharge/${userData._id}`, {
        charge: val.charge
      })
        .then(res => {
          message.success(res.message);
          closeModal('updateCharge');
          updateForm.resetFields();
          fetchCustomerDetail(effectiveUserId);
        })
        .catch(e => message.error(e.message));
    }, [userData, effectiveUserId]);
    
    const handleDueDateChange = useCallback((val) => {
      axiosService.put(`/profit/bill-date/${userData._id}`, {
        date: val
      })
        .then(res => {
          message.success(res.message);
          fetchCustomerDetail(effectiveUserId);
        })
        .catch(e => message.error(e.message));
    }, [userData, effectiveUserId]);
    
    // Password change handler
    const handlePasswordChange = useCallback((values) => {
      // Validate that new password and confirm password match
      if (values.newPassword !== values.confirmPassword) {
        message.error('New password and confirm password do not match');
        return;
        }
      
      axiosService.put(`/auth/update-password`, {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword
      })
        .then(res => {
          message.success(res.message);
          closeModal('changePassword');
            passwordForm.resetFields();
            fetchCustomerDetail(effectiveUserId);
        })
        .catch(e => message.error(e.message));
    }, [userData]);
    
    // Document preview handler
    const imagePreviewOpen = useCallback((doc, rec) => {
      let url = getDocURL(`user_doc/${rec}/${doc}`);
      let ext = doc.split('.')[1];
      let imgex = ['jpg', 'jpeg', 'png'];
      
      if (imgex.includes(ext)) {
        setPreviewImg(url);
        setPreviewShow(true);
      } else {
        window.open(url, "_blank");
      }
    }, []);
    
    // Memoized back link
    const backLink = useMemo(() => {
      if (user?.role === "ADMIN") return '/users';
      if (user?.role === "WHITE_LABEL") return `/wl/users?${searchParams}`;
      return `/dashboard?${searchParams}`;
    }, [user, searchParams]);
    
    // Memoized summary blocks for dashboard
    const summaryBlocks = useMemo(() => {
      if (!profitData) return null;
      
      return [
        {
          icon: icon1,
          value: profitData.accountBalance.toLocaleString("hi-IN"),
          label: "Total Deposit Balance",
          color: "red"
        },
        {
          icon: icon2,
          value: profitData.totalProfit.toLocaleString("hi-IN"),
          label: "Total Profit",
          color: "yellow"
        },
        {
          icon: icon3,
          value: profitData.yourProfit.toLocaleString("hi-IN"),
          label: "Your Profit",
          color: "green"
        },
        {
          icon: icon4,
          value: profitData.botCharge.toLocaleString("hi-IN"),
          label: "Bot Charge",
          color: "purple"
        },
        {
          icon: icon4,
          value: (profitData.accountBalance + profitData.totalProfit).toLocaleString("hi-IN"),
          label: "Total Account balance",
          color: "purple"
        },
        {
          icon: icon3,
          value: profitData.totalwithdraw.toLocaleString("hi-IN"),
          label: "Total Withdraw",
          color: "green"
        },
        {
          icon: icon1,
          value: profitData.withdrawProfit.toLocaleString("hi-IN"),
          label: "Pending Withdraw From Profit",
          color: "red"
        }
      ];
    }, [profitData]);
    
    return (
        <div style={{paddingBottom: "30px"}}>
            <div>
                <Link to={backLink} style={{fontSize: 18}}><LeftOutlined /> Back</Link>
            </div>
            
            {/* Image Preview */}
            <Image
                width={200}
                style={{ display: 'none' }}
                src={previewImg}
                preview={{
                    visible: previewShow,
                    src: previewImg,
                    onVisibleChange: (value) => {
                        setPreviewShow(value);
                    },
                }}
            />
            
            {/* Modals */}
            <EditModal 
                title="Edit Request"
                open={modalState.editRequest}
                onCancel={() => closeModal('editRequest')}
                onFinish={handleEditRequestSubmit}
                form={editRequestForm}
            >
                <Form.Item label="Request" name="request">
                    <Input.TextArea placeholder="Edit Request" />
                </Form.Item>
            </EditModal>
            
            <EditModal 
                title="Update File Name"
                open={modalState.updateFile}
                onCancel={() => closeModal('updateFile')}
                onFinish={handleFileSubmit}
                form={updateForm}
            >
                <Form.Item label="File Name" name="fileName">
                    <Select
                        placeholder="Select File"
                        options={fileList.map(dt => ({
                            label: dt.file,
                            value: dt._id
                        }))}
                    />
                </Form.Item>
            </EditModal>
            
            <EditModal 
                title="Update Setting"
                open={modalState.updateSetting}
                onCancel={() => closeModal('updateSetting')}
                onFinish={handleSettingSubmit}
                form={updateForm}
            >
                <Form.Item label="Setting" name="setting">
                    <Select
                        placeholder="Select Setting"
                        options={[
                            { value: "5-10", label: "5-10%" },
                            { value: "10-15", label: "10-15%" },
                            { value: "15-20", label: "15-20%" }
                        ]}
                    />
                </Form.Item>
            </EditModal>
            
            <EditModal 
                title="Update Charge PR"
                open={modalState.updateCharge}
                onCancel={() => closeModal('updateCharge')}
                onFinish={handleChargeSubmit}
                form={updateForm}
            >
                <Form.Item label="Charge" name="charge">
                    <InputNumber placeholder='Enter Charge Amount' style={{width: "100%"}} min={1} max={100}/>
                </Form.Item>
            </EditModal>
            
            {/* Password Change Modal */}
            <EditModal 
                title="Change Password"
                open={modalState.changePassword}
                onCancel={() => closeModal('changePassword')}
                onFinish={handlePasswordChange}
                form={passwordForm}
            >
                <Form.Item 
                    label="Current Password" 
                    name="currentPassword"
                    rules={[{ required: true, message: 'Please enter your current password' }]}
                >
                    <Input.Password placeholder="Enter current password" />
                </Form.Item>
                <Form.Item 
                    label="New Password" 
                    name="newPassword"
                    rules={[
                        { required: true, message: 'Please enter a new password' },
                    ]}
                >
                    <Input.Password placeholder="Enter new password" />
                </Form.Item>
                <Form.Item 
                    label="Confirm Password" 
                    name="confirmPassword"
                    rules={[
                        { required: true, message: 'Please confirm your new password' },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('newPassword') === value) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error('Passwords do not match'));
                            },
                        }),
                    ]}
                >
                    <Input.Password placeholder="Confirm new password" />
                </Form.Item>
            </EditModal>
            
            {(user?.role === 'CUSTOMER') && (
                <Flex justify='space-between' style={{marginBottom: 20}}>
                    <Button 
                        type='link' 
                        onClick={() => openModal('changePassword')}
                    >
                        Change Password
                    </Button>
                    <Button type='primary' onClick={() => openModal('editRequest')}>Edit Profile Request</Button>
                </Flex>
            )}
            
            {/* Dashboard Summary */}
            {(user?.role === 'ADMIN' || user?.role === 'WHITE_LABEL') && summaryBlocks && (
                <Card style={{marginBottom: 20}}>
                    <Row gutter={[30,30]}>
                        {summaryBlocks.map((block, index) => (
                            <Col xl={6} lg={12} md={24} key={index}>
                                <div className={`summery-block ${block.color}`}>
                                    <div className={`icon ${block.color}`}><img src={block.icon} alt={block.label} /></div>
                                    <Typography.Title level={2}>{block.value}</Typography.Title>
                                    <Typography.Text>{block.label}</Typography.Text>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Card>
            )}
            
            {/* User Profile Content */}
            <Row gutter={16}>
                <Col span={8}>
                    {/* Personal Info Card */}
                    <Card style={{marginBottom: 15}}>
                        <Typography.Title level={5} style={{textAlign: "center"}}>Personal Info</Typography.Title>
                        <List itemLayout="horizontal">
                            <List.Item>
                                <Typography.Paragraph className='list-text'>
                                    <Typography.Text strong>Full Name: </Typography.Text>{userData?.user?.fullName}
                                </Typography.Paragraph>
                            </List.Item>
                            <List.Item>
                                <Typography.Paragraph className='list-text'>
                                    <Typography.Text strong>Email (Username): </Typography.Text>{userData?.user?.fullName}
                                </Typography.Paragraph>
                            </List.Item>
                            <List.Item>
                                <Typography.Paragraph className='list-text'>
                                    <Typography.Text strong>Password: </Typography.Text>{userData?.user?.password}
                                </Typography.Paragraph>
                            </List.Item>
                            <List.Item>
                                <Typography.Paragraph className='list-text'>
                                    <Typography.Text strong>Status: </Typography.Text>{userData?.user?.status}
                                </Typography.Paragraph>
                            </List.Item>
                        </List>
                    </Card>
                    
                    {/* Broker Info Card */}
                    <Card style={{marginBottom: 15}} color='#FFE2E5'>
                        <Typography.Title level={5} style={{textAlign: "center"}}>MT5 Details</Typography.Title>
                        <List itemLayout="horizontal">
                            <List.Item>
                                <Typography.Paragraph className='list-text'>
                                    <Typography.Text strong>Broker Name: </Typography.Text>{userData?.broker_name}
                                </Typography.Paragraph>
                            </List.Item>
                            <List.Item>
                            <Typography.Paragraph className='list-text'>
                                    <Typography.Text strong>Investor's Password: </Typography.Text>{userData?.investor_password}
                                </Typography.Paragraph>
                            </List.Item>
                            <List.Item>
                            <Typography.Paragraph className='list-text'>
                                    <Typography.Text strong>Master Password: </Typography.Text>{userData?.master_password}
                            </Typography.Paragraph>
                            </List.Item>
                            {/* <List.Item>
                                <Typography.Paragraph className='list-text'>
                                    <Typography.Text strong>IB Name: </Typography.Text>{userData?.broker?.ib_name}
                                </Typography.Paragraph>
                            </List.Item>
                            <List.Item>
                                <Typography.Paragraph className='list-text'>
                                    <Typography.Text strong>IB Link: </Typography.Text>{userData?.broker?.ib_link}
                                </Typography.Paragraph>
                            </List.Item>
                            <List.Item>
                                <Typography.Paragraph className='list-text'>
                                    <Typography.Text strong>Portal Link: </Typography.Text>{userData?.broker?.portal_link}
                                </Typography.Paragraph>
                            </List.Item> */}
                        </List>
                    </Card>
                    
                    {/* Documents Card */}
                    {/* <Card>
                        <Typography.Title level={5}>Documents</Typography.Title>
                        <Space>
                            {userData?.docs?.map((d, i) => (
                                <div className='doc-card' key={i}>
                                    <Flex>
                                        <Button type="text" onClick={() => imagePreviewOpen(d, userData?.cust_code)}>
                                            <EyeOutlined />
                                        </Button>
                                        <Button type="text" onClick={() => window.open(getDocURL(`user_doc/${userData?.cust_code}/${d}`))}>
                                            <DownloadOutlined />
                                        </Button>
                                    </Flex>
                                </div>
                            ))}
                        </Space>
                    </Card> */}
                </Col>
                
                <Col span={8}>
                    {/* Other Info Card */}
                    <Card style={{marginBottom: 15}}>
                        <Typography.Title level={5} style={{textAlign: "center"}}>Other Info</Typography.Title>
                        <List itemLayout="horizontal">
                            <List.Item>
                                <Typography.Paragraph className='list-text'>
                                    <Typography.Text strong>Customer Code: </Typography.Text>{userData?.cust_code}
                                </Typography.Paragraph>
                            </List.Item>
                            <List.Item>
                                <Typography.Paragraph className='list-text'>
                                    <Typography.Text strong>Account Number: </Typography.Text>{userData?.account_number}
                                </Typography.Paragraph>
                            </List.Item>
                            <List.Item>
                                <Typography.Paragraph className='list-text'>
                                    <Typography.Text strong>Business Email: </Typography.Text>{userData?.business_email}
                                </Typography.Paragraph>
                            </List.Item>
                            <List.Item>
                                <Typography.Paragraph className='list-text'>
                                    <Typography.Text strong>Phone Number: </Typography.Text>{userData?.contact_no?.phone_no}
                                </Typography.Paragraph>
                            </List.Item>
                            <List.Item>
                                <Typography.Paragraph className='list-text'>
                                    <Typography.Text strong>Whatsapp Number: </Typography.Text>{userData?.contact_no?.whatsapp_no}
                                </Typography.Paragraph>
                            </List.Item>
                            <List.Item>
                                <Typography.Paragraph className='list-text'>
                                    <Typography.Text strong>Date of Birth: </Typography.Text>{userData?.dob ? moment(userData.dob).format("DD-MM-YYYY") : ''}
                                </Typography.Paragraph>
                            </List.Item>
                            {/* <List.Item>
                                <Typography.Paragraph className='list-text'>
                                    <Typography.Text strong>Investor's Password: </Typography.Text>{userData?.investor_password}
                                </Typography.Paragraph>
                            </List.Item>
                            <List.Item>
                                <Typography.Paragraph className='list-text'>
                                    <Typography.Text strong>Master Password: </Typography.Text>{userData?.master_password}
                                </Typography.Paragraph>
                            </List.Item>
                            <List.Item>
                                <Typography.Paragraph className='list-text'>
                                    <Typography.Text strong>Portal Link: </Typography.Text>{userData?.portal_link}
                                </Typography.Paragraph>
                            </List.Item>
                            <List.Item>
                                <Typography.Paragraph className='list-text'>
                                    <Typography.Text strong>Portal Password: </Typography.Text>{userData?.portal_password}
                                </Typography.Paragraph>
                            </List.Item> */}
                            <List.Item>
                                <Typography.Paragraph className='list-text'>
                                    <Typography.Text strong>Reference By: </Typography.Text>{userData?.reference_by?.fullName}
                                </Typography.Paragraph>
                            </List.Item>
                            <List.Item>
                                <Typography.Paragraph className='list-text'>
                                    <Typography.Text strong>Server: </Typography.Text>{userData?.server}
                                </Typography.Paragraph>
                            </List.Item>
                        </List>
                    </Card>
                    
                    <Row gutter={16}>
                        {/* VPS Server Card - Admin Only */}
                        {user?.role === 'ADMIN' && (
                            <Col span={12}>
                                <Card style={{marginBottom: 15}}>
                                    <Typography.Title level={5} style={{textAlign: "center"}}>VPS Server</Typography.Title>
                                    <List itemLayout="horizontal">
                                        <List.Item>
                                            <Typography.Paragraph className='list-text'>
                                                <Typography.Text strong>VPS IP: </Typography.Text>{userData?.server_vps?.vps_ip}
                                            </Typography.Paragraph>
                                        </List.Item>
                                        <List.Item>
                                            <Typography.Paragraph className='list-text'>
                                                <Typography.Text strong>Username: </Typography.Text>{userData?.server_vps?.username}
                                            </Typography.Paragraph>
                                        </List.Item>
                                        <List.Item>
                                            <Typography.Paragraph className='list-text'>
                                                <Typography.Text strong>Password: </Typography.Text>{userData?.server_vps?.password}
                                            </Typography.Paragraph>
                                        </List.Item>
                                    </List>
                                </Card>
                            </Col>
                        )}
                        
                    </Row>
                </Col>
                <Col span={8}>
                            <Card>
                                <Typography.Title level={5} style={{textAlign: "center"}}>Bot Performance</Typography.Title>
                                <List itemLayout="horizontal">
                                    <List.Item>
                                        <Typography.Paragraph className='list-text'>
                                            <Typography.Text strong>File Name: </Typography.Text>{ userData?.white_label === "67e1c06eff0ae7b8ae5ef8ee" ? "PIP EA 1.0" : userData?.file_name?.file}
                                            {user?.role === "ADMIN" && (
                                                <Button onClick={() => openModal('updateFile')} type='text' icon={<EditOutlined />} />
                                            )}
                                        </Typography.Paragraph>
                                    </List.Item>
                                    <List.Item>
                                        <Typography.Paragraph className='list-text'>
                                            <Typography.Text strong>Bot Setting: </Typography.Text>{userData?.setting}%
                                            {user?.role === "ADMIN" && (
                                                <Button onClick={() => openModal('updateSetting')} type='text' icon={<EditOutlined />} />
                                            )}
                                        </Typography.Paragraph>
                                    </List.Item>
                                    <List.Item>
                                        <Typography.Paragraph className='list-text'>
                                            <Typography.Text strong>Bot Charge: </Typography.Text>
                                            {userData?.bot_charge && `${userData.bot_charge}%`}
                                            {editAllowRole.includes(user?.role) && (
                                                <Button onClick={() => openModal('updateCharge')} type='text' icon={<EditOutlined />} />
                                            )}
                                        </Typography.Paragraph>
                                    </List.Item>
                                    {user?.role === 'ADMIN' && (
                                        <List.Item>
                                            <Typography.Paragraph className='list-text'>
                                                <Typography.Text strong>Due Date: </Typography.Text>
                                                <Select
                                                    onChange={handleDueDateChange}
                                                    value={userData?.bill_due_date}
                                                    style={{width: "190px"}}
                                                    options={[
                                                        { value: "", label: "None" },
                                                        { value: "5", label: "5th day of the month" },
                                                        { value: "10", label: "10th day of the month" },
                                                        { value: "15", label: "15th day of the month" }
                                                    ]}
                                                />
                                            </Typography.Paragraph>
                                        </List.Item>
                                    )}
                                </List>
                            </Card>
                </Col>
            </Row>
        </div>
    );
};

const mapStateToProps = ({login, loading}) => {
    const {user, customer} = login;
    const {isLoading} = loading;
    return {user, customer, isLoading};
};

export default connect(mapStateToProps, {}) (UserProfile);
